/**
 * La période, et ce qu'on en dit — la logique de l'écran d'effort (§6.4).
 *
 * Séparée du composant parce que c'est la seule partie qui **décide** quelque
 * chose : quelles dates une présélection recouvre, comment une durée se dit, ce
 * qu'un histogramme vaut quand il est vide. Le reste est du balisage, et un test
 * de rendu y mesurerait React.
 */

/** Une durée en secondes, dite comme un humain la dit. */
export function duree(secondes: number | null | undefined): string | null {
  if (secondes === null || secondes === undefined) return null;
  if (secondes < 60) return `${Math.round(secondes)} s`;
  const minutes = Math.round(secondes / 60);
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  return reste === 0 ? `${heures} h` : `${heures} h ${String(reste).padStart(2, "0")}`;
}

// Le choix d'une période vit dans `ui/periode` depuis le 6 septembre 2026 : la
// carte de l'enfant demande la même chose, et la coquille enfant n'importe pas
// `parent/`. Réexporté pour les appelants existants.
export { bornes, jour, PRESELECTIONS, rentree } from "@/ui/periode";
export type { Periode, Preselection } from "@/ui/periode";

// `enClair` vit dans `ui/dates` depuis le 3 septembre 2026 : le journal de
// l'enfant écrit les mêmes dates, et la coquille enfant n'importe pas `parent/`.
// Réexporté pour les appelants existants.
export { enClair } from "@/ui/dates";

/**
 * Ce qu'on écrit au-dessus d'un total de temps.
 *
 * **Une durée partielle se dit partielle.** La coquille n'envoie une durée que
 * depuis le 30 août 2026 ; une période qui remonte plus loin en mélange donc qui
 * en ont une et d'autres pas, et « 3 h 20 » sur quarante exercices dont douze
 * chronométrés est un chiffre juste qui raconte une chose fausse. Le parent doit
 * lire sur quoi porte le total, sinon il le prendra pour le tout.
 */
export function assiseDuTemps(chronometres: number, exercices: number): string | null {
  if (exercices === 0 || chronometres === 0) return null;
  if (chronometres >= exercices) return null;
  return `sur ${chronometres} exercice${chronometres > 1 ? "s" : ""} chronométré${
    chronometres > 1 ? "s" : ""
  } sur ${exercices}`;
}

export type Seau = { key: number; exercises: number; at_least?: boolean };

/** Le libellé d'un seau : « 3 » ou « 3 et plus », jamais l'un pour l'autre. */
export function seauLibelle(seau: Seau, unite: (n: number) => string): string {
  return seau.at_least ? `${unite(seau.key)} et plus` : unite(seau.key);
}

/**
 * La largeur d'une barre, en pourcentage du plus grand seau.
 *
 * Rapportée au **maximum de l'histogramme** et non au total : sur quatre seaux
 * dont un écrase les autres, une échelle en part du total donnerait trois traits
 * indiscernables de zéro. Et un seau non vide ne descend jamais sous 2 % —
 * « un exercice » et « aucun exercice » doivent se distinguer à l'œil.
 */
export function largeur(seau: Seau, seaux: Seau[]): number {
  const sommet = Math.max(...seaux.map((s) => s.exercises), 0);
  if (sommet === 0 || seau.exercises === 0) return 0;
  return Math.max(2, (seau.exercises / sommet) * 100);
}

/** La part des exercices résolus, en toutes lettres — jamais un pourcentage nu. */
export function resolution(resolus: number, exercices: number): string | null {
  if (exercices === 0) return null;
  return `${resolus} résolu${resolus > 1 ? "s" : ""} sur ${exercices}`;
}
